<template>
  <MainScreen :hero-items="heroItems" :hero-title="heroTitle" />
  <RecentlyViewed :key="currencyRefreshKey" />
  <ReviewCollectionContainer />
  <PopularTargets />
  <ToursList :key="currencyRefreshKey" />
  <SeasonContainer
    v-if="seasonItems.length"
    :items="seasonItems"
    :is-loaded="isDataLoaded"
    :title="seasonTitle"
    :subtitle="seasonSubtitle"
    :color="seasonColor"
  />
  <GuidesContainer v-if="guidesItems.length" :items="guidesItems" :title="guidesTitle" :subtitle="guidesSubtitle" />
  <ReviewsContainer />
  <TrustSection />
  <FaqSection />
  <JournalContainer
    v-if="articlesItems.length"
    :items="articlesItems"
    :is-loaded="isDataLoaded"
    :title="articlesTitle"
    :subtitle="articlesSubtitle"
  />
  <FeedSection :key="currencyRefreshKey" />
  <CategoryContainer />
  <SubscriptionBlock :api-url="VITE_API_URL" :analytics-api-url="VITE_RUNTIME_SOGU_SERVICE_URL" />
</template>

<script lang="ts" setup>
import { subscribeDisplayCurrencyChanged } from '@experience/core-display-currency-lib';
import { useI18n } from '@experience/i18n';
import { SubscriptionBlock } from '@experience/subscription-block-package';
import { useHead } from '@unhead/vue';
import { onBeforeUnmount, ref, onBeforeMount } from 'vue';

import { useDeferredPageview } from '@/composables/useDeferredPageview';
import { useInverseHeader } from '@/composables/useInverseHeader';
import CategoryContainer from '@/features/CategoryContainer.vue';
import GuidesContainer from '@/features/GuidesContainer.vue';
import JournalContainer from '@/features/JournalContainer.vue';
import MainScreen from '@/features/MainScreen.vue';
import RecentlyViewed from '@/features/RecentlyViewed.vue';
import ReviewCollectionContainer from '@/features/review-collection/ReviewCollectionContainer.vue';
import ReviewsContainer from '@/features/ReviewsContainer.vue';
import SeasonContainer from '@/features/SeasonContainer.vue';
import ToursList from '@/features/ToursList.vue';
import { useMainPageBlocks } from '@/pages/home/composables/useMainPageBlocks';
import ru from '@/pages/home/i18n/ru';
import { buildHomePageJsonLd } from '@/pages/home/lib/buildHomePageJsonLd';
import type { MainHeroItem } from '@/shared/api/generated/api';
import { getRuntimeConfig } from '@/shared/config/runtime';
import { getServerPreloadedData } from '@/shared/helpers/preloadedData';
import { useAnalytics } from '@/shared/lib/analytics';
import { useRenderReport } from '@/shared/lib/lifecycle';
import FaqSection from '@/widgets/FaqSection.vue';
import FeedSection from '@/widgets/FeedSection.vue';
import PopularTargets from '@/widgets/PopularTargets.vue';
import TrustSection from '@/widgets/TrustSection.vue';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const VITE_API_URL = import.meta.env.VITE_API_URL;
const { VITE_RUNTIME_SOGU_SERVICE_URL } = getRuntimeConfig();
const { trackEventImmediately } = useAnalytics();

useInverseHeader();
useRenderReport();

const {
  guidesItems,
  guidesTitle,
  guidesSubtitle,
  articlesItems,
  articlesTitle,
  articlesSubtitle,
  seasonItems,
  seasonTitle,
  seasonSubtitle,
  seasonColor,
  heroItems,
  heroTitle,
  isDataLoaded,
  fetchBlocks,
} = useMainPageBlocks({
  guidesTitle: t('guides.title'),
  guidesSubtitle: t('guides.subtitle'),
  articlesTitle: t('articles.title'),
  articlesSubtitle: t('articles.subtitle'),
});

const currencyRefreshKey = ref(0);

const unsubscribeDisplayCurrencyChanged = subscribeDisplayCurrencyChanged(() => {
  currencyRefreshKey.value += 1;
});

onBeforeUnmount(unsubscribeDisplayCurrencyChanged);

onBeforeMount(() => {
  // SSR-предзагрузка отдаёт только первый hero-слайд (для быстрого первого
  // рендера картинки); полный список слайдов приходит из BFF через обычный
  // fetchBlocks() ниже и перезапишет heroItems, когда отработает main_hero-блок.
  const preloadedHero = getServerPreloadedData() as MainHeroItem | undefined;
  if (preloadedHero) {
    heroItems.value = [preloadedHero];
  }
});

useDeferredPageview({
  beforePageview: () => void fetchBlocks(),
  pageview: () => void trackEventImmediately('main.pageview'),
});

useHead({
  title: t('seo.title'),
  link: [
    {
      href: `${window.location.origin}/`,
      rel: 'canonical',
    },
  ],
  meta: [
    {
      content: t('seo.description'),
      name: 'description',
    },
    {
      content: t('seo.description'),
      name: 'og:description',
    },
    {
      property: 'og:image',
      // Статичная OG-картинка главной страницы (CDN Трипстера), задаётся вручную.
      content: 'https://static2.tripster.ru/img/preview2.jpg',
    },
    {
      property: 'og:url',
      content: `${window.location.origin}/`,
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(
        buildHomePageJsonLd({
          // Канонический прод-домен главной для schema.org (жёстко задан, не зависит от origin).
          url: 'https://experience.tripster.ru/',
          name: 'Tripster',
          alternateName: [t('seo.alternateName'), 'Tripster.ru'],
        }),
      ),
    },
  ],
});
</script>
